import { useEffect, useState } from "react";
import { Archive, Copy, Download, Link2, Pencil, Send, Trash2, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { eur, INVOICE_STATUS, QUOTE_BILLING, QUOTE_EDITABLE_STATUSES, QUOTE_STATUS, VALIDATION_MODE_LABELS, quoteNumber } from "./docUtils";

const KIND_LABEL = { acompte: "Facture d'acompte", solde: "Facture finale", standard: "Facture" };
const fmtDT = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)} à ${iso.slice(11, 16).replace(":", "h")}` : "—");
const fmtD = (iso) => (iso || "").slice(0, 10) || "—";
const MODES = [["email", "E-mail"], ["whatsapp", "WhatsApp"], ["sms", "Message / SMS"], ["telephone", "Téléphone"], ["rdv", "Rendez-vous / Oral"], ["autre", "Autre"]];

export default function QuoteDetailDrawer({ quoteId, onClose, onChanged, onEdit, onSend, onOpenInvoice }) {
  const [q, setQ] = useState(null);
  const [validateOpen, setValidateOpen] = useState(false);
  const [mode, setMode] = useState("");
  const [modeDetail, setModeDetail] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => quoteId && api.get(`/accounting/quotes/${quoteId}`).then((r) => setQ(r.data)).catch(() => {});
  useEffect(() => { setQ(null); load(); }, [quoteId]);
  const refresh = () => { load(); onChanged(); };

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const openPdf = async () => {
    const r = await api.get(`/accounting/quotes/${q.id}/pdf`, { responseType: "blob" });
    window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
  };

  const validate = () => act(async () => {
    await api.post(`/accounting/quotes/${q.id}/validate`, { mode, detail: modeDetail.trim() });
    setValidateOpen(false); setMode(""); setModeDetail("");
  }, "Devis passé en Validé — il est désormais verrouillé");

  const st = q ? (QUOTE_STATUS[q.status] || QUOTE_STATUS.envoye) : null;
  const bill = q?.billing;
  const billSt = bill ? QUOTE_BILLING[bill.status] : null;
  const editable = q && QUOTE_EDITABLE_STATUSES.includes(q.status);
  const acompte = q && q.acompte_type !== "aucun" && (bill?.acompte_prevu || 0) > 0;
  const depInv = (q?.linked_invoices || []).find((i) => i.kind === "acompte");
  const depPaid = depInv && (depInv.paid_total || 0) >= (depInv.total_ttc || 0) - 0.01;

  return (
    <Dialog open={!!quoteId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-detail" aria-describedby={undefined}>
        {!q ? <div className="p-8 text-center text-sm text-neutral-400">Chargement…</div> : (
          <>
            <DialogTitle className="font-heading font-bold text-lg flex items-center gap-2 flex-wrap">
              {quoteNumber(q)}
              <span className={`text-[11px] rounded-full px-2.5 py-1 border ${st.cls}`} data-testid="quote-detail-status">{st.label}</span>
              {billSt && <span className={`text-[11px] rounded-full px-2.5 py-1 border ${billSt.cls}`} data-testid="quote-billing-status">{billSt.label}</span>}
            </DialogTitle>
            <p className="text-sm text-neutral-500">{q.client_name}</p>

            <div className="space-y-5 mt-3">
              <div className="border border-neutral-200 rounded-xl overflow-hidden" data-testid="quote-detail-items">
                {q.items.map((it, i) => (
                  <div key={i} className="flex justify-between gap-3 px-4 py-2.5 text-sm border-b border-neutral-100 last:border-0">
                    <div className="min-w-0">
                      <p className="font-medium whitespace-pre-line">{it.prestation}</p>
                      {it.description && <p className="text-xs text-neutral-400 whitespace-pre-line">{it.description}</p>}
                    </div>
                    <p className="text-neutral-600 shrink-0">{it.qty} × {eur(it.price_ht)}</p>
                  </div>
                ))}
                <div className="bg-neutral-50 px-4 py-3 text-sm space-y-1">
                  {(q.discount_percent || 0) > 0 && (
                    <>
                      <div className="flex justify-between"><span className="text-neutral-500">Montant initial HT</span><span>{eur(q.total_ht_brut)}</span></div>
                      <div className="flex justify-between"><span className="text-neutral-500">Remise ({q.discount_percent} %)</span><span className="text-red-600">− {eur(q.discount_ht)}</span></div>
                    </>
                  )}
                  <div className="flex justify-between"><span className="text-neutral-500">Total HT</span><span>{eur(q.total_ht)}</span></div>
                  {q.tva_mode !== "non_assujetti" && <div className="flex justify-between"><span className="text-neutral-500">TVA</span><span>{eur(q.total_tva)}</span></div>}
                  <div className="flex justify-between font-bold text-base"><span>Total TTC</span><span data-testid="quote-detail-total">{eur(q.total_ttc)}</span></div>
                </div>
              </div>

              {acompte && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3.5 text-sm space-y-1" data-testid="quote-acompte-block">
                  <div className="flex justify-between"><span>Acompte {q.acompte_type === "pourcent" ? `de ${q.acompte_value} %` : "demandé"}</span><strong>{eur(bill.acompte_prevu)}</strong></div>
                  <div className="flex justify-between text-blue-800"><span>Solde restant</span><strong>{eur(q.total_ttc - bill.acompte_prevu)}</strong></div>
                  {depInv && <p className="text-xs text-blue-700 pt-1">Acompte : {eur(depInv.total_ttc)} — {depPaid ? "Payé" : "En attente de règlement"}</p>}
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm" data-testid="quote-traceability">
                <div><p className="text-[11px] uppercase tracking-wider text-neutral-400">Émission</p><p className="font-medium">{fmtD(q.sent_at || q.created_at)}</p></div>
                <div><p className="text-[11px] uppercase tracking-wider text-neutral-400">Validité</p><p className="font-medium">{q.validity_days || 30} jours</p></div>
                {q.validated_at && (
                  <div><p className="text-[11px] uppercase tracking-wider text-neutral-400">Validé le</p>
                    <p className="font-medium" data-testid="quote-validated-at">{fmtDT(q.validated_at)}</p>
                    <p className="text-xs text-neutral-500" data-testid="quote-validation-mode">
                      Mode : {q.validation_mode === "autre" ? q.validation_mode_detail : VALIDATION_MODE_LABELS[q.validation_mode] || "—"}
                    </p>
                  </div>
                )}
                {q.refused_at && <div><p className="text-[11px] uppercase tracking-wider text-neutral-400">Refusé le</p><p className="font-medium">{fmtDT(q.refused_at)}</p></div>}
                {q.cancelled_at && <div><p className="text-[11px] uppercase tracking-wider text-neutral-400">Annulé le</p><p className="font-medium">{fmtDT(q.cancelled_at)}</p></div>}
              </div>

              {(q.mod_requests || []).length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3.5 space-y-2" data-testid="quote-mod-requests">
                  <p className="text-[11px] uppercase tracking-wider text-yellow-700 font-semibold">Demandes de modification du client</p>
                  {q.mod_requests.map((m) => (
                    <div key={m.id} className="text-sm">
                      <p className="text-neutral-800 whitespace-pre-line">{m.message}</p>
                      <p className="text-[11px] text-yellow-600">{fmtDT(m.at)}</p>
                    </div>
                  ))}
                </div>
              )}
              {q.refusal_reason && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3.5 text-sm" data-testid="quote-refusal-reason">
                  <p className="text-[11px] uppercase tracking-wider text-red-700 font-semibold mb-1">Motif du refus</p>
                  <p className="text-neutral-800 whitespace-pre-line">{q.refusal_reason}</p>
                </div>
              )}

              {bill && q.status === "accepte" && (
                <div className="border border-neutral-200 rounded-xl p-4 space-y-2" data-testid="quote-billing-summary">
                  <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-semibold">Suivi de facturation</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                    <div><p className="text-neutral-400 text-xs">Montant validé</p><p className="font-medium">{eur(bill.montant_valide)}</p></div>
                    <div><p className="text-neutral-400 text-xs">Déjà facturé</p><p className="font-medium">{eur(bill.deja_facture)}</p></div>
                    <div><p className="text-neutral-400 text-xs">Déjà payé</p><p className="font-medium text-green-700">{eur(bill.deja_paye)}</p></div>
                    <div><p className="text-neutral-400 text-xs">Reste à facturer</p><p className="font-medium text-orange-600">{eur(bill.reste_a_facturer)}</p></div>
                    <div><p className="text-neutral-400 text-xs">Reste à payer</p><p className="font-medium">{eur(bill.reste_a_payer)}</p></div>
                  </div>
                </div>
              )}

              {(q.linked_invoices || []).length > 0 && (
                <div className="space-y-1.5" data-testid="quote-linked-invoices">
                  <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-semibold">Facturation liée</p>
                  {q.linked_invoices.map((inv) => {
                    const ist = INVOICE_STATUS[inv.status] || INVOICE_STATUS.brouillon;
                    return (
                      <button key={inv.id} onClick={() => onOpenInvoice(inv.id)} data-testid={`quote-linked-invoice-${inv.id}`}
                        className="w-full flex justify-between items-center text-sm bg-neutral-50 hover:bg-neutral-100 border border-neutral-200 rounded-lg px-3 py-2 transition-colors text-left">
                        <span>{KIND_LABEL[inv.kind] || "Facture"} {inv.number ? `N° ${inv.number}` : "(brouillon)"}</span>
                        <span className="flex items-center gap-2">
                          <span className="font-medium">{eur(inv.total_ttc)}</span>
                          <span className={`text-[10px] rounded-full px-2 py-0.5 border ${ist.cls}`}>{ist.label}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="flex items-center gap-2" data-testid="quote-public-link">
                <Input readOnly value={q.public_url || ""} className="text-xs text-neutral-500" onFocus={(e) => e.target.select()} />
                <Button variant="outline" size="sm" className="gap-1.5 shrink-0" data-testid="quote-copy-link"
                  onClick={() => { navigator.clipboard.writeText(q.public_url); toast.success("Lien client copié"); }}>
                  <Link2 className="w-3.5 h-3.5" /> Copier
                </Button>
              </div>

              <div className="flex flex-wrap gap-2 pt-1" data-testid="quote-actions">
                {editable && (
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => onEdit(q)} data-testid="quote-action-edit">
                    <Pencil className="w-3.5 h-3.5" /> Modifier
                  </Button>
                )}
                {editable && (
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => onSend(q)} data-testid="quote-action-send">
                    <Send className="w-3.5 h-3.5" /> {q.status === "brouillon" ? "Aperçu & envoi" : "Renvoyer"}
                  </Button>
                )}
                {!["accepte", "annule"].includes(q.status) && (
                  <>
                  <Button variant="outline" size="sm" className="gap-1.5" data-testid="quote-action-present"
                    onClick={() => act(() => api.post(`/accounting/quotes/${q.id}/presenter`), "Devis présenté dans l'espace client (sans email)")}>
                    <Send className="w-4 h-4" /> Présenter dans l'espace client
                  </Button>
                  <Button size="sm" className="gap-1.5 bg-green-700 hover:bg-green-800 text-white" onClick={() => setValidateOpen(true)} data-testid="quote-action-validate">
                    Passer le devis en Validé
                  </Button>
                  </>
                )}
                {q.status === "accepte" && !acompte && !q.full_invoice_id && (
                  <Button size="sm" className="gap-1.5 bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="quote-action-full-invoice"
                    onClick={() => act(async () => { const r = await api.post(`/accounting/quotes/${q.id}/full-invoice`); onOpenInvoice(r.data.id); }, "Facture créée en brouillon")}>
                    Créer la facture
                  </Button>
                )}
                {q.status === "accepte" && acompte && !q.deposit_invoice_id && (
                  <Button size="sm" className="gap-1.5 bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="quote-action-deposit-invoice"
                    onClick={() => act(async () => { const r = await api.post(`/accounting/quotes/${q.id}/deposit-invoice`); onOpenInvoice(r.data.id); }, "Facture d'acompte créée en brouillon")}>
                    Créer la facture d'acompte
                  </Button>
                )}
                {q.status === "accepte" && q.deposit_invoice_id && !q.balance_invoice_id && (
                  <Button size="sm" className="gap-1.5 bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="quote-action-balance-invoice"
                    onClick={() => act(async () => { const r = await api.post(`/accounting/quotes/${q.id}/balance-invoice`); onOpenInvoice(r.data.id); }, "Facture finale créée en brouillon")}>
                    Créer la facture finale
                  </Button>
                )}
                <Button variant="outline" size="sm" className="gap-1.5" onClick={openPdf} data-testid="quote-action-pdf">
                  <Download className="w-3.5 h-3.5" /> PDF
                </Button>
                <Button variant="outline" size="sm" className="gap-1.5" data-testid="quote-action-duplicate"
                  onClick={() => act(async () => { const r = await api.post(`/accounting/quotes/${q.id}/duplicate`); toast.success(`Devis N° ${r.data.number} créé (brouillon)`); }, null)}>
                  <Copy className="w-3.5 h-3.5" /> Dupliquer
                </Button>
                {q.status !== "brouillon" && (
                  <Button variant="outline" size="sm" className="gap-1.5" data-testid="quote-action-archive"
                    onClick={() => act(() => api.post(`/accounting/quotes/${q.id}/archive`, { archived: !q.archived }), q.archived ? "Devis restauré" : "Devis archivé")}>
                    <Archive className="w-3.5 h-3.5" /> {q.archived ? "Restaurer" : "Archiver"}
                  </Button>
                )}
                {q.status === "accepte" && (
                  <Button variant="outline" size="sm" className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50" onClick={() => setCancelOpen(true)} data-testid="quote-action-cancel">
                    <XCircle className="w-3.5 h-3.5" /> Annuler le devis
                  </Button>
                )}
                {(q.status === "annule" || q.status === "brouillon") && (
                  <Button variant="outline" size="sm" className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50" onClick={() => setDeleteOpen(true)} data-testid="quote-action-delete">
                    <Trash2 className="w-3.5 h-3.5" /> Supprimer
                  </Button>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer" data-testid="quote-visible-toggle">
                <input type="checkbox" className="rounded" checked={q.visible_client !== false}
                  onChange={(e) => act(() => api.post(`/accounting/quotes/${q.id}/visibility`, { visible: e.target.checked }),
                    e.target.checked ? "Devis visible dans l'espace client" : "Devis masqué de l'espace client")} />
                Visible dans l'espace client
                {q.visible_client === false && <span className="text-[11px] text-neutral-400">(actuellement masqué au client)</span>}
              </label>
            </div>
          </>
        )}
      </DialogContent>

      <Dialog open={validateOpen} onOpenChange={setValidateOpen}>
        <DialogContent className="max-w-md" data-testid="quote-validate-dialog" aria-describedby={undefined}>
          <DialogTitle className="font-heading font-bold">Passer le devis en Validé</DialogTitle>
          <div className="space-y-3 mt-2">
            <Label className="text-xs">Mode de validation *</Label>
            <div className="grid grid-cols-2 gap-2">
              {MODES.map(([v, l]) => (
                <button key={v} onClick={() => setMode(v)} data-testid={`validate-mode-${v}`}
                  className={`text-sm border rounded-lg px-3 py-2 text-left transition-colors ${mode === v ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200 hover:border-neutral-400"}`}>
                  {l}
                </button>
              ))}
            </div>
            {mode === "autre" && (
              <Input value={modeDetail} onChange={(e) => setModeDetail(e.target.value)} placeholder="Précisez le mode…" data-testid="validate-mode-detail" />
            )}
            <p className="text-xs text-neutral-500 bg-neutral-50 border border-neutral-200 rounded-lg p-2.5">
              Confirmer la validation de ce devis ? Une fois validé, le devis sera verrouillé et ne pourra plus être modifié.
            </p>
            <Button disabled={!mode || (mode === "autre" && !modeDetail.trim()) || busy} onClick={validate}
              className="w-full bg-green-700 hover:bg-green-800 text-white" data-testid="validate-confirm-btn">
              Confirmer la validation
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-md" data-testid="quote-cancel-dialog" aria-describedby={undefined}>
          <DialogTitle className="font-heading font-bold">Annuler le devis</DialogTitle>
          <p className="text-sm text-neutral-600 mt-2">
            Confirmer l'annulation de ce devis ? Le devis annulé ne sera plus pris en compte dans les statistiques financières.
          </p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setCancelOpen(false)} className="flex-1">Revenir</Button>
            <Button disabled={busy} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="cancel-confirm-btn"
              onClick={() => act(async () => { await api.post(`/accounting/quotes/${q.id}/cancel`); setCancelOpen(false); }, "Devis annulé — exclu des statistiques")}>
              Confirmer l'annulation
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-md" data-testid="quote-delete-dialog" aria-describedby={undefined}>
          <DialogTitle className="font-heading font-bold">Supprimer le devis</DialogTitle>
          <p className="text-sm text-neutral-600 mt-2">
            Supprimer définitivement ce devis {q?.status === "annule" ? "annulé" : "brouillon"} ? Cette action est irréversible.
          </p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteOpen(false)} className="flex-1">Revenir</Button>
            <Button disabled={busy} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="delete-confirm-btn"
              onClick={() => act(async () => { await api.delete(`/accounting/quotes/${q.id}`); setDeleteOpen(false); onClose(); }, "Devis supprimé définitivement")}>
              Supprimer définitivement
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
